import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useAppSelector } from '../../store';
import { StepCard } from './StepCard';
import type { TimelineStep } from '@repancy/core';
import { testIds } from '../../lib/testIds';

interface StreamingOutputProps {
  sessionId: string;
}

const containerStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  borderStyle: 'solid',
  borderWidth: 2,
  borderTopColor: '#808080',
  borderLeftColor: '#808080',
  borderBottomColor: '#dfdfdf',
  borderRightColor: '#dfdfdf',
  background: '#fff',
  padding: 6,
  minHeight: 200,
};

const emptyStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  minHeight: 160,
  fontFamily: "var(--xp-font-body)",
  fontSize: 12,
  color: '#808080',
  fontStyle: 'italic',
};

const generatingStyle: React.CSSProperties = {
  fontFamily: "var(--xp-font-body)",
  fontSize: 12,
  color: 'var(--xp-selection)',
  padding: '8px 6px',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

export function StreamingOutput({ sessionId }: StreamingOutputProps) {
  const stepIds = useAppSelector((s) => s.timelines.steps.ids);
  const stepEntities = useAppSelector((s) => s.timelines.steps.entities);
  const steps = useMemo<TimelineStep[]>(
    () =>
      stepIds
        .map((id) => stepEntities[id])
        .filter((st): st is TimelineStep => !!st && st.sessionId === sessionId),
    [stepIds, stepEntities, sessionId],
  );

  const currentIndex = useAppSelector((s) => s.timelines.currentStepIndex);
  const sessionStatus = useAppSelector(
    (s) => s.promptSessions.sessions.entities[sessionId]?.status,
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Auto-scroll to bottom on new steps
  const prevCountRef = useRef(steps.length);
  useEffect(() => {
    if (steps.length > prevCountRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    prevCountRef.current = steps.length;
  }, [steps.length]);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleFork = useCallback((_stepId: string) => {
    // Fork logic dispatched from parent -- placeholder
  }, []);

  const isRunning = sessionStatus === 'Running';

  if (steps.length === 0 && !isRunning) {
    return (
      <div style={containerStyle}>
        <div style={emptyStyle}>Run a prompt to see output here</div>
      </div>
    );
  }

  return (
    <div style={containerStyle} ref={scrollRef} data-testid={testIds.promptLab.outputArea}>
      {steps.map((step, idx) => (
        <StepCard
          key={step.id}
          step={step}
          isActive={idx === currentIndex}
          isExpanded={expandedIds.has(step.id)}
          onToggle={() => toggleExpand(step.id)}
          onFork={() => handleFork(step.id)}
        />
      ))}

      {isRunning && (
        <div style={generatingStyle} data-testid={testIds.promptLab.generating}>
          <span style={{ animation: 'none' }}>
            {'Generating'}
            <AnimatedDots />
          </span>
        </div>
      )}
    </div>
  );
}

/** Simple animated dots without CSS keyframes */
function AnimatedDots() {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setCount((c) => (c + 1) % 4), 400);
    return () => clearInterval(interval);
  }, []);

  return <span>{'.'.repeat(count)}</span>;
}
